import { Link } from "react-router";
import { docsPath } from "./docsNav";

export function DocsCoreConceptsPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Getting started</p>
      <h1 className="docs-page-title">Core concepts</h1>
      <p className="docs-lede">
        The vocabulary for the active Woven Host managed path, followed by the capabilities that
        remain Preview.
      </p>

      <h2 className="docs-heading">Woven Host lifecycle</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Product</dt>
          <dd>
            The console resource you create and allocate capacity to. Its product ID is distinct
            from the Woven protocol namespace and session IDs assigned at provisioning.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Capacity allocation</dt>
          <dd>
            Capacity = tick rate (Hz) × allocated CCU (concurrent users). New sign-ins remain guest
            with zero capacity until approved. The approved Lite default is 250 capacity units,
            shared across products; Pro defaults to 2,500 units. Custom approved budgets are
            preserved.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Running</dt>
          <dd>
            The lifecycle state that confirms the managed runtime is ready for descriptor reveal and
            client admission. Treat Running as the authoritative readiness signal.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Connection descriptor</dt>
          <dd>
            The sensitive connection material explicitly revealed for a Running product. Use its
            native or browser endpoint, trust information, Bearer credential, and assigned protocol
            identifiers as returned rather than deriving replacements from the product ID.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Realtime connection</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>
            <Link to={docsPath("sdks/rust")} className="docs-inline-link">
              Rust client
            </Link>
          </dt>
          <dd>
            The Rust <code>woven-client</code> connects to the descriptor&apos;s native QUIC
            endpoint with TLS verification enabled.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("sdks/javascript")} className="docs-inline-link">
              TypeScript client
            </Link>
          </dt>
          <dd>
            The Online browser client carries WVN1 over WebTransport and HTTP/3/QUIC to the same
            managed runtime through the descriptor&apos;s separate <code>webTransport.url</code>{" "}
            endpoint.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("authentication")} className="docs-inline-link">
              Bearer authentication
            </Link>
          </dt>
          <dd>
            The client presents the descriptor&apos;s Bearer credential during the secure
            connection. It is runtime admission material, separate from the identity used to sign in
            to the Woven Host console.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("sessions")} className="docs-inline-link">
              Managed admission
            </Link>
          </dt>
          <dd>
            The runtime validates the managed credential and admits the connection to the namespace
            and session assigned by Woven Host. The descriptor supplies those IDs.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("spatial-subspaces")} className="docs-inline-link">
              Logical space
            </Link>
          </dt>
          <dd>
            The descriptor returns fixed logical spaces plus complete managed 3D Spatial Subspace
            definitions. Host allocates numeric IDs from 3, enforces AABB bounds, and permits at
            most 64 add-only spatial definitions per server generation.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("events")} className="docs-inline-link">
              Channel and Ephemeral events
            </Link>
          </dt>
          <dd>
            The descriptor returns the fixed channel used by the managed session. Its events are
            Ephemeral: connected recipients receive live traffic, and the event lifetime ends with
            the runtime state.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Availability</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Lite</dt>
          <dd>
            The active entry tier: a 250-unit default capacity budget, managed product lifecycle,
            descriptor reveal, native Rust QUIC, browser WebTransport, the TypeScript browser
            client, and managed spatial routing.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Pro and Dedicated</dt>
          <dd>Preview tiers offered through private founder access first.</dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("persistent-state")} className="docs-inline-link">
              Persistence
            </Link>
          </dt>
          <dd>
            Ephemeral events are the active managed data model. Persistence beyond Ephemeral remains
            Preview.
          </dd>
        </div>
        <div className="detail-row">
          <dt>
            <Link to={docsPath("inference-network")} className="docs-inline-link">
              Inference
            </Link>
          </dt>
          <dd>Inference is Preview, with private founder access first.</dd>
        </div>
      </dl>

      <Link to={docsPath("quickstart")} className="btn btn--ghost docs-continue-btn">
        Follow the quickstart &rarr;
      </Link>
    </div>
  );
}
